import {Component, OnInit} from '@angular/core';
import { Nanium } from "nanium/core";
import { NaniumConsumerBrowserHttp } from "nanium/managers/consumers/browserHttp";
import { WebRequestInterceptorService } from "../services/security/web-request-interceptor.service";
import { WebAuthenticationService } from "../services/security/web-authentication.service";
import {NavigationEnd, Router} from "@angular/router";
import { User } from "../../../server/entities/user.entity";
import {
  BaseSecurityUserHasPermissionRequest
} from "../../../server/services/base/security/userHasPermission.contract";
import { userPermissions } from "../../../server/entities/user-role.entity";
import {FacilityQueryRequest} from "../../../server/services/facility/query.contract";
import {FacilityUpdateRequest} from "../../../server/services/facility/update.contract";
import {NewsQueryRequest} from "../../../server/services/news/query.contract";
import {GatheringQueryRequest} from "../../../server/services/gathering/query.contract";
import {UserQueryRequest} from "../../../server/services/user/query.contract";
import {UserUpdateRequest} from "../../../server/services/user/update.contract";
import {NewsUpdateRequest} from "../../../server/services/news/update.contract";
import {GatheringUpdateRequest} from "../../../server/services/gathering/update.contract";
import {SharedDataService} from "../services/shared-data/shared-data.service";
import { PageViewTrackingService } from '../services/analytics/page-view-tracking.service';
import {BroadcastMessageServiceRequest} from "../../../server/services/firebase/broadcastMessage.contract";
import {
  GeolocationGetCoordinatesFromAddressRequest
} from "../../../server/services/geolocation/getCoordinatesFromAddress.contract";
import dayjs from "dayjs";

@Component({
  selector: 'app-root',
  templateUrl: 'app.component.html',
  styleUrls: ['app.component.scss'],
})
export class AppComponent implements OnInit {
  public user: User;

  public showAdminMenu: boolean = false;
  public ownedFacility: string;

  // Number of owned facilities that still need the onboarding wizard; drives
  // the badge next to the "deine Einrichtung" menu entry.
  public pendingOnboardingCount: number = 0;

  public adminPages = [
    {
      title: 'Dashboard',
      url: '/admin/dashboard',
      icon: 'speedometer',
      subOptions: []
    },
    {
      title: 'Nutzer*innen',
      url: '/admin/users',
      icon: 'person',
      subOptions: []
    },
    {
      title: 'Einrichtungen',
      url: '/admin/facilities',
      icon: 'storefront',
      subOptions: [
        { title: 'Neue Einrichtung erstellen', url: '/admin/facilities/create', icon: 'add-circle', isCurrentPage: false },
        { title: 'Zur Aktualisierung einladen', url: '/admin/facilities/onboarding-campaign', icon: 'mail', isCurrentPage: false }
      ]
    },
    {
      title: 'Beratungskategorien',
      url: '/admin/consulting-categories',
      icon: 'albums',
      subOptions: [
        { title: 'Neue Kategorie erstellen', url: '/admin/consulting-categories/create', icon: 'add-circle', isCurrentPage: false }
      ]
    },
    // { title: 'Beratungsangebote', url: '/admin/consulting-offers', icon: 'book' },
    {
      title: 'Veranstaltungen',
      url: '/admin/gatherings',
      icon: 'calendar',
      subOptions: [
        { title: 'Neue Veranstaltung erstellen', url: '/admin/gatherings/create', icon: 'add-circle', isCurrentPage: false }
      ]
    },
    {
      title: 'News',
      url: '/admin/news',
      icon: 'newspaper',
      subOptions: [
        { title: 'News-Artikel erstellen', url: '/admin/news/create', icon: 'add-circle', isCurrentPage: false }
      ]
    },
    {
      title: 'Pulses',
      url: '/admin/life-hacks',
      icon: 'basketball',
      subOptions: [
        { title: 'Neuen Pulse erstellen', url: '/admin/life-hacks/create', icon: 'add-circle', isCurrentPage: false }
      ]
    },
    {
      title: 'Push-Benachrichtigungen',
      url: '/admin/push-notifications',
      icon: 'notifications',
      subOptions: []
    },
    {
      title: 'Analytics',
      url: '/admin/analytics',
      icon: 'bar-chart',
      subOptions: []
    },
    {
      title: 'Angebote-Sync',
      url: '/admin/next-sync',
      icon: 'sync',
      subOptions: []
    },
    // { title: 'für Macher*innen', url: '/admin/participation-offers', icon: 'bulb' },
  ];

  public creatorPages = [
    {
      title: 'Dashboard',
      url: '/admin/dashboard',
      icon: 'speedometer',
      subOptions: []
    },
    {
      title: 'deine Einrichtung',
      url: `/admin/facilities/my-facility`,
      icon: 'storefront',
      subOptions: []
    },
    {
      title: 'deine Subscriber',
      url: '/admin/users',
      icon: 'person',
      subOptions: []
    },
    // { title: 'deine Beratungsangebote', url: '/admin/consulting-offers', icon: 'book' },
    {
      title: 'deine Veranstaltungen',
      url: '/admin/gatherings',
      icon: 'calendar',
      subOptions: [
        { title: 'Neue Veranstaltung erstellen', url: '/admin/gatherings/create', icon: 'add-circle', isCurrentPage: false }
      ]
    },
    {
      title: 'deine News',
      url: '/admin/news',
      icon: 'newspaper',
      subOptions: [
        { title: 'News-Artikel erstellen', url: '/admin/news/create', icon: 'add-circle', isCurrentPage: false }
      ]
    },
    // { title: 'deine Life Hacks', url: '/admin/life-hacks', icon: 'basketball' }
  ];

  constructor(
    private webRequestInterceptorService: WebRequestInterceptorService,
    private webAuthenticationService: WebAuthenticationService,
    private router: Router,
    private sharedDataService: SharedDataService,
    private pageViewTrackingService: PageViewTrackingService,
  ) {
  }

  async ngOnInit() {
    let serverBaseUrl: string = '';
    if (location.port === '4202') {
      serverBaseUrl = 'http://' + location.host.replace(':4202', ':3001');
    }
    if (location.port === '44343') {
      serverBaseUrl = 'http://' + location.host.replace(':44343', ':3001');
    }
    await Nanium.addManager(new NaniumConsumerBrowserHttp({
      apiUrl: serverBaseUrl + '/api',
      apiEventUrl: serverBaseUrl + '/events',
      onServerConnectionRestored: () => {
        console.log('server connection restored');
        // when the clients could not reach the server for some time,
        // you should reload all stuff you need because maybe you missed some events
      },
      eventSubscriptionSendInterceptors: [],
      requestInterceptors: [ this.webRequestInterceptorService ],
      handleError: async (error: any) => {
        if (
          error === 'unauthorized'
          || error instanceof Error && error.message == 'unauthorized'
        ) {
          await this.router.navigate(['/login']);
        }
      }
    }));

    this.pageViewTrackingService.init();

    await this.webAuthenticationService.init();
    this.user = await this.webAuthenticationService.getUser();

    if (await this.hasAdminPermission()) {
      this.showAdminMenu = true;
    }

    // Update facility sub-options for creator users
    this.updateFacilitySubOptions();
    this.refreshPendingOnboardingCount();

    this.router.events.subscribe((e) => {
      if (e instanceof NavigationEnd) {
        this.reloadUser();
      }
    });

    if (location.pathname === '/') {
      if (this.user) {
        await this.router.navigate(['/admin/dashboard']);
      } else {
        await this.router.navigate(['/login']);
      }
    }
  }

  async logout() {
    await this.webAuthenticationService.logout();
    // Reset component state after logout
    this.user = null;
    this.showAdminMenu = false;
  }

  async reloadUser() {
    if (location.pathname === '/reset-password') {
      return;
    }

    // Always try to get the current user, regardless of previous state
    console.log('reloading user');
    this.user = await this.webAuthenticationService.getUser();

    // Update admin menu visibility based on current user's permissions
    if (this.user) {
      this.showAdminMenu = await this.hasAdminPermission();
    } else {
      this.showAdminMenu = false;
    }

    this.updateFacilitySubOptions();
    this.refreshPendingOnboardingCount();
  }

  private async refreshPendingOnboardingCount() {
    if (!this.user) {
      this.pendingOnboardingCount = 0;
      return;
    }
    try {
      const pending = await this.sharedDataService.getPendingOnboardingFacilities(this.user);
      this.pendingOnboardingCount = pending.length;
    } catch (error) {
      console.warn('AppComponent: pending onboarding check failed:', error);
      this.pendingOnboardingCount = 0;
    }
  }

  async hasAdminPermission() {
    if (!this.user) {
      return false;
    }
    return await new BaseSecurityUserHasPermissionRequest({
      permission: userPermissions.accessAdminBackend
    }).execute();
  }

  async compressImages() {
    let facilities = await new FacilityQueryRequest().execute();
    for (let facility of facilities) {
      console.log(`compressing facility ${facility.name}`);
      if (facility.logo) {
        facility.logo = await this.rescaleImage(facility.logo, 160);
      }
      if (facility.titlePicture) {
        facility.titlePicture = await this.rescaleImage(facility.titlePicture, 400);
      }
      await new FacilityUpdateRequest(facility).execute();
    }

    let news = await new NewsQueryRequest().execute();
    for (let newsItem of news) {
      if (newsItem.titleImage) {
        console.log(`compressing news ${newsItem.headline}`);
        newsItem.titleImage = await this.rescaleImage(newsItem.titleImage, 400);
        await new NewsUpdateRequest(newsItem).execute();
      }
    }

    let gatherings = await new GatheringQueryRequest().execute();
    for (let gathering of gatherings) {
      if (gathering.titlePicture) {
        console.log(`compressing gathering ${gathering.name}`);
        gathering.titlePicture = await this.rescaleImage(gathering.titlePicture, 400);
        await new GatheringUpdateRequest(gathering).execute();
      }
    }

    let users = await new UserQueryRequest().execute();
    for (let user of users) {
      if (user.profilePicture) {
        console.log(`deleting picture of user ${user.username}`);
        user.profilePicture = '';
        await new UserUpdateRequest(user).execute();
      }
    }
  }

  rescaleImage(src, maxWidth): Promise<string> {
    return new Promise((res, rej) => {
      // SVGs are vector images — rasterising them to JPEG would destroy their
      // scalability, so leave them untouched (the compressImages migration runs
      // over every facility logo / title picture).
      if (typeof src === 'string' && src.startsWith('data:image/svg+xml')) {
        res(src);
        return;
      }
      const img = new Image();
      img.src = src;
      img.onload = () => {
        let {newWidth, newHeight} = this.computeNewImageSize(img, maxWidth);

        const elem = document.createElement('canvas');
        elem.width = newWidth;
        elem.height = newHeight;

        const ctx = elem.getContext('2d');
        ctx.drawImage(img, 0, 0, newWidth, newHeight);
        const data = ctx.canvas.toDataURL('image/jpeg');
        res(data);
      }
      img.onerror = error => rej(error);
    })
  }

  private computeNewImageSize(img: HTMLImageElement, maxWidth) {
    const oldWidth = img.width;
    const oldHeight = img.height;
    let newWidth: number;
    if (oldWidth < maxWidth) {
      newWidth = oldWidth;
    } else {
      newWidth = maxWidth;
    }
    let resizeFactor: number = oldWidth / newWidth;
    const newHeight: number = oldHeight / resizeFactor;
    return {newWidth, newHeight};
  }

  // Nominatim usage policy: max 1 request/second. We wait slightly longer than
  // 1s between calls to stay safely below the limit even with clock drift.
  private static readonly NOMINATIM_REQUEST_INTERVAL_MS = 1100;

  async backfillFacilityLocations() {
    const facilities = await new FacilityQueryRequest().execute();
    const candidates = facilities.filter(f =>
      (!f.location?.latitude || !f.location?.longitude)
      && f.address?.street && f.address?.postCode && f.address?.city
    );
    const skippedNoAddress = facilities.filter(f =>
      (!f.location?.latitude || !f.location?.longitude)
      && (!f.address?.street || !f.address?.postCode || !f.address?.city)
    ).length;

    if (candidates.length === 0) {
      alert(
        `Keine Einrichtungen ohne Standort gefunden, die geocodiert werden können.`
        + (skippedNoAddress > 0 ? `\n\n${skippedNoAddress} Einrichtung(en) haben weder Adresse noch Standort.` : '')
      );
      return;
    }

    if (!confirm(
      `${candidates.length} Einrichtung(en) ohne Standort gefunden.\n`
      + `Es wird pro Einrichtung ca. 1 Sekunde benötigt (Nominatim-Ratelimit).\n`
      + `Geschätzte Dauer: ${Math.ceil(candidates.length * AppComponent.NOMINATIM_REQUEST_INTERVAL_MS / 1000)} Sekunden.\n\n`
      + `Fortfahren?`
    )) {
      return;
    }

    let updated = 0;
    const failures: { name: string, reason: string }[] = [];

    for (let i = 0; i < candidates.length; i++) {
      const facility = candidates[i];

      if (i > 0) {
        await new Promise(resolve => setTimeout(resolve, AppComponent.NOMINATIM_REQUEST_INTERVAL_MS));
      }

      console.log(`[${i + 1}/${candidates.length}] geocoding facility ${facility.name}`);
      try {
        const location = await new GeolocationGetCoordinatesFromAddressRequest({
          address: facility.address
        }).execute();

        if (!location?.latitude || !location?.longitude) {
          failures.push({ name: facility.name, reason: 'Keine Koordinaten gefunden' });
          continue;
        }

        facility.location = location;
        // this is by definition an automatic write — also self-heals a stale
        // manual flag that carried no coordinates
        facility.locationManuallySet = false;
        await new FacilityUpdateRequest(facility).execute();
        updated++;
      } catch (e) {
        console.error(`error geocoding facility ${facility.name}`, e);
        failures.push({ name: facility.name, reason: (e?.message || String(e)) });
      }
    }

    let summary = `Standorte ergänzt: ${updated}/${candidates.length}`;
    if (skippedNoAddress > 0) {
      summary += `\nÜbersprungen (keine Adresse): ${skippedNoAddress}`;
    }
    if (failures.length > 0) {
      summary += `\n\nFehlgeschlagen (${failures.length}):\n`
        + failures.map(f => `- ${f.name}: ${f.reason}`).join('\n');
    }
    alert(summary);
  }

  private static readonly MIN_GATHERING_DURATION_HOURS = 2;

  async fixGatheringEndDates() {
    const gatherings = await new GatheringQueryRequest().execute();
    const minDurationMs = AppComponent.MIN_GATHERING_DURATION_HOURS * 60 * 60 * 1000;
    const broken = gatherings.filter(g => {
      if (!g.startingAt || !g.endingAt) return false;
      const start = new Date(g.startingAt).getTime();
      const end = new Date(g.endingAt).getTime();
      return end - start < minDurationMs;
    });

    if (broken.length === 0) {
      alert('Keine Veranstaltungen mit fehlerhaftem Enddatum gefunden.');
      return;
    }

    if (!confirm(
      `${broken.length} Veranstaltung(en) mit Ende vor Start oder zu kurzer Dauer gefunden.\n`
      + `Das Enddatum wird auf den selben Tag wie der Start gesetzt (Endzeit bleibt erhalten, sofern dadurch eine Mindestdauer von ${AppComponent.MIN_GATHERING_DURATION_HOURS}h eingehalten wird; sonst wird Ende = Start + ${AppComponent.MIN_GATHERING_DURATION_HOURS}h gesetzt).\n\n`
      + `Fortfahren?`
    )) {
      return;
    }

    let updated = 0;
    const failures: { name: string, reason: string }[] = [];

    for (const gathering of broken) {
      try {
        const start = dayjs(gathering.startingAt);
        const minEnd = start.add(AppComponent.MIN_GATHERING_DURATION_HOURS, 'hour');

        // Try to preserve the original end time-of-day on the start's date.
        const oldEnd = dayjs(gathering.endingAt);
        let proposedEnd = start
          .hour(oldEnd.hour())
          .minute(oldEnd.minute())
          .second(oldEnd.second())
          .millisecond(oldEnd.millisecond());

        if (proposedEnd.isBefore(minEnd)) {
          proposedEnd = minEnd;
        }

        const proposedEndIso = proposedEnd.toISOString();
        console.log(`fixing gathering ${gathering.name}: ${gathering.endingAt} -> ${proposedEndIso}`);
        gathering.endingAt = proposedEndIso;
        await new GatheringUpdateRequest(gathering).execute();
        updated++;
      } catch (e) {
        console.error(`error fixing gathering ${gathering.name}`, e);
        failures.push({ name: gathering.name, reason: (e?.message || String(e)) });
      }
    }

    let summary = `Enddaten korrigiert: ${updated}/${broken.length}`;
    if (failures.length > 0) {
      summary += `\n\nFehlgeschlagen (${failures.length}):\n`
        + failures.map(f => `- ${f.name}: ${f.reason}`).join('\n');
    }
    alert(summary);
  }

  async preprocessFacilityDescriptions() {
    let facilities = await new FacilityQueryRequest().execute();
    for (let facility of facilities) {
      // check if facility description contains an url that isn't a link
      if (facility?.description?.match(/href/)) {
        console.log('ignored: ', facility.description);
        continue;
      }
      if (facility?.description?.match(/http[^<\s]+/)) {
        const newDescription = facility?.description?.replace(/http[^<\s]+/g, (match) => {
          return `<a href="${match}">${match}</a>`;
        });
        console.log('processing:', facility.description, facility?.description?.match(/http[^<\s]+/g), newDescription);
        try {
          facility.description = newDescription;
          await new FacilityUpdateRequest(facility).execute();
        } catch (e) {
          console.error('error updating facility', e);
          return;
        }
      }
    }
  }

  shouldShowSubOptions(pageUrl: string): boolean {
    const currentUrl = this.router.url;
    const page = this.adminPages.find(p => p.url === pageUrl) || this.creatorPages.find(p => p.url === pageUrl);

    if (page && page.subOptions) {
      page.subOptions.forEach(subOption => {
        subOption.isCurrentPage = currentUrl === subOption.url;
      });
    }

    return currentUrl.startsWith(pageUrl);
  }

  getFacilitySubOptions() {
    if (!this.user || !this.user.ownedFacilities || this.user.ownedFacilities.length === 0) {
      return [];
    }

    // Get current route to determine which contextual sub-options to show
    const currentPath = this.router.url;
    const subOptions = [];

    // Show contextual navigation based on current route
    if (currentPath.includes('/admin/facilities/facility/')) {
      subOptions.push({
        title: 'Einrichtungsinfo',
        url: currentPath,
        icon: 'information-circle',
        isCurrentPage: true
      });
    } else if (currentPath.includes('/admin/facilities/update/')) {
      subOptions.push({
        title: 'Einrichtung bearbeiten',
        url: currentPath,
        icon: 'pencil',
        isCurrentPage: true
      });
    }

    return subOptions;
  }

  updateFacilitySubOptions() {
    // Find the "deine Einrichtung" page and update its sub-options
    const facilityPage = this.creatorPages.find(page => page.title === 'deine Einrichtung');
    if (facilityPage) {
      facilityPage.subOptions = this.getFacilitySubOptions();
    }
  }

  async sendTestBroadcast() {
    try {
      console.log('Sending test broadcast message...');

      const response = await new BroadcastMessageServiceRequest({
        message: {
          title: 'Yoggl hat neue Inhalte',
          body: 'Schau doch mal wieder rein!',
          data: {
            action: 'navigate',
            url: '/home' // Link to home page in mobile app
          }
        }
      }).execute();

      if (response.success) {
        console.log('Test broadcast sent successfully!', response);
        alert(`Test-Broadcast erfolgreich gesendet!\nNachrichten versucht: ${response.messagesAttempted}\nNachrichten erfolgreich: ${response.messagesSucceeded}\nNachrichten fehlgeschlagen: ${response.messagesFailed}`);
      } else {
        console.error('Failed to send test broadcast:', response.errors);
        alert('Fehler beim Senden des Test-Broadcasts: ' + (response.errors?.join(', ') || 'Unbekannter Fehler'));
      }
    } catch (error) {
      console.error('Error sending test broadcast:', error);
      alert('Fehler beim Senden des Test-Broadcasts: ' + error.message);
    }
  }
}
